<template>
  <UiDrawer v-model:aperto="ui.menuAperto" :aria-label="t('layout.menu.principale')" larghezza="19rem">
    <template #header>
      <div class="flex items-center px-5 pt-5 pb-4"><AppLogo wordmark compatto /></div>
    </template>

    <div class="flex min-h-0 flex-1 flex-col gap-3">
      <LayoutSidebarRicerca ref="ricercaRef" v-model="ricerca" @invio="vaiAlRisultato" />
      <LayoutSidebarRecenti v-if="!ricerca" :recenti="recentRoutes" />
      <nav class="min-h-0 flex-1 overflow-x-hidden overflow-y-auto px-2 pb-3" :aria-label="t('layout.menu.principale')" data-test="sidebar-nav">
        <ul class="flex flex-col gap-0.5">
          <LayoutSidebarVoce
            v-for="(voce, indice) in vociFiltrate"
            :key="voce.key"
            class="motion-safe:animate-drawer-voce"
            :style="{ animationDelay: `${80 + Math.min(indice, 12) * 32}ms` }"
            :voce="voce"
            :livello="0"
            :route-attiva="route.path"
            :espansi="espansi"
            @alterna="alternaGruppo"
            @naviga="ui.chiudiMenu"
          />
        </ul>
        <p v-if="ricerca && !vociFiltrate.length" class="px-3 py-4 text-sm text-text-muted" role="status">{{ t("layout.menu.nessun_risultato") }}</p>
      </nav>
    </div>

    <template #footer>
      <LayoutSidebarComandi :in-attesa="comandiAttivi.inCorso" @apri="apriComandiAttivi" />
      <LayoutSidebarUtente
        :username="auth.username"
        :iniziali="auth.iniziali"
        :avatar-src="auth.avatarSrc"
        :amministratore="auth.amministratore"
        @esci="auth.esci()"
        @naviga="ui.chiudiMenu"
      />
    </template>
  </UiDrawer>
</template>

<script setup lang="ts">
// Menu laterale dell'app (S1 del ridisegno): pannello modale con ricerca, pagine recenti, albero di navigazione, comandi manuali
// attivi e scheda utente con uscita (tema e lingua stanno nella barra globale). Si apre dal pulsante in testa a ogni pagina o con Ctrl+B; Ctrl+K cerca.
// Stato in `useUiStore` (aperto/chiuso, tema) e `useAuthStore` (menu, profilo, uscita); logica in `useSidebar`.
import { useI18n } from "vue-i18n";
import { trovaPerRoute } from "~/utils/menu";

const { t } = useI18n();
const route = useRoute();
const ui = useUiStore();
const auth = useAuthStore();
const { toastError } = useToastHelper();
const { recentRoutes, addRoute } = useRecentRoutes();
const comandiAttivi = useComandiAttiviStore();

const ricercaRef = ref<{ focus: () => void } | null>(null);
const voci = computed(() => auth.menuVisibile);
const routeAttiva = computed(() => route.path);
const { ricerca, espansi, vociFiltrate, alternaGruppo, apriCatenaAttiva, invioRicerca } = useSidebar(voci, routeAttiva, t);

function vaiAlRisultato(): void {
  const destinazione = invioRicerca();
  if (!destinazione) return;
  ui.chiudiMenu();
  void navigateTo(destinazione);
}

// Il pannello dei comandi è un altro livello modale: prima si chiude il menu, per non sovrapporre due finestre
function apriComandiAttivi(): void {
  ui.chiudiMenu();
  comandiAttivi.apri();
}

// Scorciatoie: Ctrl+B apre/chiude il menu, Ctrl+K lo apre e mette a fuoco la ricerca (non nelle pagine di accesso)
useEventListener(import.meta.client ? window : undefined, "keydown", (evento: KeyboardEvent) => {
  if (!evento.ctrlKey || AUTH_PAGES.includes(route.path)) return;
  const tasto = evento.key.toLowerCase();
  if (tasto === "b") {
    evento.preventDefault();
    ui.alternaMenu();
  } else if (tasto === "k") {
    evento.preventDefault();
    ui.apriMenu();
    nextTick(() => ricercaRef.value?.focus());
  }
});

// Qualunque navigazione (link, pillole recenti, ricerca, scheda utente) chiude il menu
watch(() => route.path, ui.chiudiMenu);

// Le pagine visitate si ricordano per le pillole «recenti»: voce di menu, oppure titolo dichiarato dalla pagina con definePageMeta
watch(
  () => route.path,
  (percorso) => {
    if (AUTH_PAGES.includes(percorso)) return;
    const voce = trovaPerRoute(percorso, voci.value)?.voce;
    const label = voce?.label ?? (route.meta.recentLabel as string | undefined);
    if (!label) return;
    addRoute({ path: percorso, icon: voce?.icon ?? (route.meta.recentIcon as string | undefined) ?? "fal fa-circle", label });
  },
);

onMounted(async () => {
  apriCatenaAttiva();
  if (AUTH_PAGES.includes(route.path)) return;
  // Il menu dipende dal ruolo: se non era salvato (PWA iOS, primo avvio dopo l'aggiornamento) arriva col profilo e la catena attiva si riapre
  const errore = await auth.caricaProfilo();
  apriCatenaAttiva();
  if (errore) toastError(errore);
});
</script>
